/**
 * Hash routing. No router dependency: a hash route works from a home-screen shortcut, offline,
 * with no server rewrite rules.
 *
 *   #/trips                                   trips
 *   #/trip/:tripId                            one trip: its sections
 *   #/trip/:tripId/section/:sectionId         section plan
 *   #/trip/:tripId/section/:sectionId/list    shopping list
 *   #/shop                                    the active shopping section, else a plain basket
 *   #/shop/:tripId/:sectionId                 shop that section
 *   #/recipes  #/recipe/:id                   recipes
 *   #/foods    #/food/:id   #/foods/import    foods
 *   #/availability                            region × category grid
 *   #/settings                                settings
 *
 * Any route may carry a query: `#/foods?category=noodles` → `route.query.category`.
 * An unknown hash parses to `{ name: 'not-found' }`; an empty one to trips.
 */
import { useCallback, useSyncExternalStore } from 'react'

export type Query = Record<string, string>

export type Route =
  | { name: 'trips'; query?: Query }
  | { name: 'trip'; tripId: string; query?: Query }
  | { name: 'plan'; tripId: string; sectionId: string; query?: Query }
  | { name: 'list'; tripId: string; sectionId: string; query?: Query }
  | { name: 'shop'; query?: Query }
  | { name: 'shop-section'; tripId: string; sectionId: string; query?: Query }
  | { name: 'recipes'; query?: Query }
  | { name: 'recipe'; id: string; query?: Query }
  | { name: 'foods'; query?: Query }
  | { name: 'food'; id: string; query?: Query }
  | { name: 'food-import'; query?: Query }
  | { name: 'availability'; query?: Query }
  | { name: 'settings'; query?: Query }
  | { name: 'not-found'; path: string; query?: Query }

export type RouteName = Route['name']

function decode(part: string): string {
  try {
    return decodeURIComponent(part)
  } catch {
    return part
  }
}

function parseQuery(text: string): Query | undefined {
  if (!text) return undefined
  const out: Query = {}
  for (const [k, v] of new URLSearchParams(text)) out[k] = v
  return Object.keys(out).length > 0 ? out : undefined
}

function withQuery<R extends Route>(route: R, query: Query | undefined): R {
  return query ? { ...route, query } : route
}

export function parseHash(hash: string): Route {
  const raw = hash.replace(/^#/, '')
  const q = raw.indexOf('?')
  const path = q >= 0 ? raw.slice(0, q) : raw
  const query = parseQuery(q >= 0 ? raw.slice(q + 1) : '')
  const parts = path.split('/').filter(Boolean).map(decode)
  const [a, b, c, d, e] = parts

  const route = ((): Route => {
    switch (parts.length) {
      case 0:
        return { name: 'trips' }
      case 1:
        if (a === 'trips') return { name: 'trips' }
        if (a === 'shop') return { name: 'shop' }
        if (a === 'recipes') return { name: 'recipes' }
        if (a === 'foods') return { name: 'foods' }
        if (a === 'availability') return { name: 'availability' }
        if (a === 'settings') return { name: 'settings' }
        break
      case 2:
        if (a === 'trip' && b) return { name: 'trip', tripId: b }
        if (a === 'recipe' && b) return { name: 'recipe', id: b }
        if (a === 'food' && b) return { name: 'food', id: b }
        if (a === 'foods' && b === 'import') return { name: 'food-import' }
        break
      case 3:
        if (a === 'shop' && b && c) return { name: 'shop-section', tripId: b, sectionId: c }
        break
      case 4:
        if (a === 'trip' && b && c === 'section' && d) {
          return { name: 'plan', tripId: b, sectionId: d }
        }
        break
      case 5:
        if (a === 'trip' && b && c === 'section' && d && e === 'list') {
          return { name: 'list', tripId: b, sectionId: d }
        }
        break
    }
    return { name: 'not-found', path: `/${parts.join('/')}` }
  })()
  return withQuery(route, query)
}

const enc = encodeURIComponent

/** The hash for a route, with the leading '#'. Round-trips with `parseHash`. */
export function formatRoute(route: Route): string {
  const path = ((): string => {
    switch (route.name) {
      case 'trips':
        return '/trips'
      case 'trip':
        return `/trip/${enc(route.tripId)}`
      case 'plan':
        return `/trip/${enc(route.tripId)}/section/${enc(route.sectionId)}`
      case 'list':
        return `/trip/${enc(route.tripId)}/section/${enc(route.sectionId)}/list`
      case 'shop':
        return '/shop'
      case 'shop-section':
        return `/shop/${enc(route.tripId)}/${enc(route.sectionId)}`
      case 'recipes':
        return '/recipes'
      case 'recipe':
        return `/recipe/${enc(route.id)}`
      case 'foods':
        return '/foods'
      case 'food':
        return `/food/${enc(route.id)}`
      case 'food-import':
        return '/foods/import'
      case 'availability':
        return '/availability'
      case 'settings':
        return '/settings'
      case 'not-found':
        return route.path
    }
  })()
  const qs = route.query ? new URLSearchParams(route.query).toString() : ''
  return `#${path}${qs ? `?${qs}` : ''}`
}

/** Same as `formatRoute`; reads better in `<a href={href(...)}>`. */
export const href = formatRoute

export function currentRoute(): Route {
  if (typeof window === 'undefined') return { name: 'trips' }
  return parseHash(window.location.hash)
}

/** Go somewhere. `replace` swaps the history entry instead of pushing one. */
export function navigate(to: Route | string, opts: { replace?: boolean } = {}): void {
  if (typeof window === 'undefined') return
  const hash = typeof to === 'string' ? (to.startsWith('#') ? to : `#${to}`) : formatRoute(to)
  if (window.location.hash === hash) return
  if (opts.replace) {
    window.history.replaceState(null, '', hash)
    window.dispatchEvent(new HashChangeEvent('hashchange'))
  } else {
    window.location.hash = hash
  }
}

function subscribe(onChange: () => void): () => void {
  window.addEventListener('hashchange', onChange)
  return () => window.removeEventListener('hashchange', onChange)
}

let cachedHash: string | null = null
let cachedRoute: Route = { name: 'trips' }

function snapshot(): Route {
  const hash = window.location.hash
  // useSyncExternalStore compares by reference: the same hash must give the same object.
  if (hash !== cachedHash) {
    cachedHash = hash
    cachedRoute = parseHash(hash)
  }
  return cachedRoute
}

const SERVER_ROUTE: Route = { name: 'trips' }

export function useRoute(): Route {
  return useSyncExternalStore(subscribe, snapshot, () => SERVER_ROUTE)
}

/** `const go = useNavigate()`; stable across renders. */
export function useNavigate(): (to: Route | string, opts?: { replace?: boolean }) => void {
  return useCallback((to: Route | string, opts?: { replace?: boolean }) => navigate(to, opts), [])
}

/** Which bottom-tab / side-nav item a route lives under. */
export type NavSection = 'trips' | 'shop' | 'recipes' | 'foods' | 'more'

export function navSectionOf(route: Route): NavSection {
  switch (route.name) {
    case 'trips':
    case 'trip':
    case 'plan':
    case 'list':
      return 'trips'
    case 'shop':
    case 'shop-section':
      return 'shop'
    case 'recipes':
    case 'recipe':
      return 'recipes'
    case 'foods':
    case 'food':
    case 'food-import':
      return 'foods'
    default:
      return 'more'
  }
}
